import { AppKeys, MessageType, newMsg } from "../../core/localization";
import { IHistoricalData, ILog } from "./model";

export const msgErrorFetching: MessageType = newMsg(AppKeys.Com_Error_Fetching);
export const msgChecking = newMsg(AppKeys.DeviceLogs_Checking);
export const msgMoreLogsBtn = newMsg(AppKeys.DeviceLogs_LoadMore_Btn);

const compare = (logA: ILog, logB: ILog): number =>
  new Date(logA.datetime) > new Date(logB.datetime)
    ? -1
    : new Date(logA.datetime) === new Date(logB.datetime)
    ? 0
    : 1;

export const getFirst = <T extends ILog>(logs: T[]) =>
  logs.reduce((prev: ILog | undefined, l: ILog) => {
    return !prev ? l : compare(prev, l) > 0 ? l : prev;
  }, undefined);

export const getNewstDate = <T extends ILog>(logs: T[]) => {
  const log = getFirst(logs);
  return log ? log.datetime : undefined;
};

export const getLast = <T extends ILog>(logs: T[]) =>
  logs.reduce((prev: ILog | undefined, l: ILog) => {
    return !prev ? l : compare(prev, l) < 0 ? l : prev;
  }, undefined);

export const getOldestDate = <T extends ILog>(logs: T[]) => {
  const log = getLast(logs);
  return log ? log.datetime : undefined;
};

export type AreEqualILog<T extends ILog> = (logA: T, logB: T) => boolean;
export const areEqualILog: AreEqualILog<ILog> = (logA, logB) =>
  logA.datetime === logB.datetime;

const sort = <T extends ILog>(logs: T[]) => logs.sort(compare);

const merge = <T extends ILog>(
  curLogs: T[],
  newLogs: T[],
  areEqualLog: AreEqualILog<T>
): T[] =>
  newLogs.reduce(
    (p, nl) =>
      curLogs.findIndex((l) => areEqualLog(l, nl)) === -1 ? [...p, nl] : p,
    [...curLogs]
  );

export const updateIHistorical = <T extends ILog>(
  curHist: IHistoricalData<T>,
  newHist: IHistoricalData<T>,
  areEqualLog: AreEqualILog<T>,
  inFront = false
): IHistoricalData<T> => {
  const doOverride = inFront && newHist.total > newHist.logs.length;

  const logs = sort(
    doOverride ? newHist.logs : merge(curHist.logs, newHist.logs, areEqualLog)
  );
  const newLogs = logs.length - curHist.logs.length;
  const total = doOverride
    ? newHist.total
    : inFront
    ? curHist.total + newLogs
    : curHist.total;
  return { logs, total } as IHistoricalData<T>;
};

export const removeLog = <T extends ILog>(
  curHist: IHistoricalData<T>,
  item: T,
  areEqualLog: AreEqualILog<T>
): IHistoricalData<T> => {
  const logs = curHist.logs.filter((a: T) => !areEqualLog(a, item));
  const total = logs.length;
  return { logs, total } as IHistoricalData<T>;
};